/* eslint-disable max-lines-per-function */
import { SuppressKeyboardEventParams } from '@ag-grid-community/core';
import dayjs from 'dayjs';

import { ActivityType } from '@shared/services/gql.service';
import { TableService } from '@shared/services/table.service';

import { InMonthAdjustmentGridConfigContext } from './ima-grid-config-context';
import { QuarterCloseAdjustmentGridData } from '../quarter-close-adjustments.model';

const skipNoNEditableDiscountCells = (
  colId: string,
  dData: Record<string, unknown>,
  editableTMAColumns: string[]
) => {
  const shouldSuppressDiscountCell = editableTMAColumns.includes(
    colId as keyof QuarterCloseAdjustmentGridData
  );

  // Suppress discount cell to be edited if it's in the Total section
  return dData.activity_type === ActivityType.ACTIVITY_DISCOUNT && shouldSuppressDiscountCell;
};

const skipExpenseSourceCells = (
  colId: string
): colId is keyof Pick<QuarterCloseAdjustmentGridData, 'tma_source'> => colId === 'tma_source';

const tabToNextCell = (params: SuppressKeyboardEventParams): boolean => {
  params.api.clearFocusedCell();

  setTimeout(() => {
    const editableColumns = params.api
      .getAllDisplayedColumns()
      .filter((col) => !!col.getColDef().editable);

    const currentColumnId = params.column.getColId();
    const index = editableColumns.findIndex((col) => col.getColId() === currentColumnId);
    const isInRowRange = index < editableColumns.length - 1 && index !== -1;

    const findNearestRowToTab = (rowIndex: number): number => {
      const nextIndex = rowIndex + 1;

      const isRootRowLvl = !!params.api.getDisplayedRowAtIndex(nextIndex)?.aggData;

      return isRootRowLvl ? findNearestRowToTab(nextIndex) : nextIndex;
    };

    const nextColumn = isInRowRange ? editableColumns[index + 1] : editableColumns[0];
    const rowIndex = isInRowRange
      ? params.node.rowIndex
      : findNearestRowToTab(params.node.rowIndex ?? 0);

    const nextRow = rowIndex ?? 0;
    const nextCol = nextColumn.getColId() ?? '';

    params.api.clearRangeSelection();
    params.api.startEditingCell({
      colKey: nextCol,
      rowIndex: nextRow,
    });
  }, 100);

  return false;
};

// TODO: Need to remove it and use https://www.ag-grid.com/angular-data-grid/cell-editing/, with suppressKeyboardEvent editableColumn or editable doesn't work properly
export const suppressKeyboardEvent =
  (ctx: InMonthAdjustmentGridConfigContext) =>
  (params: SuppressKeyboardEventParams): boolean => {
    if (params.event.key === 'Tab') {
      return tabToNextCell(params);
    }

    if (!params.editing) {
      switch (params.event.key) {
        // delete button code 46 - backspace button code 8
        case 'Backspace':
        case 'Delete':
          if (!ctx.editMode()) {
            return true;
          }

          TableService.clearCellRange(
            params,
            (_, startIndex, endIndex) => {
              let columnsToClear: string[] = [];

              if (ctx.editableVeColumns.includes(params.column.getColId())) {
                columnsToClear = [
                  'vendor_estimate_amount',
                  'vendor_estimate_amount_ltd',
                  'vendor_estimate_percentage',
                  'vendor_estimate_unit',
                  'vendor_estimate_percent_ltd',
                ];
              } else if (params.column.getColId() === 'historical_adjustment_amount') {
                columnsToClear = ['historical_adjustment_amount'];
              } else if (
                ['tma_amount', 'tma_percentage', 'tma_unit'].includes(params.column.getColId())
              ) {
                columnsToClear = ['tma_percentage', 'tma_unit', 'tma_amount'];
              }

              TableService.clearCells(
                startIndex,
                endIndex,
                columnsToClear,
                params.api,
                (colId: string, dData) =>
                  skipNoNEditableDiscountCells(colId, dData, ctx.qcaComponent.editableTMAColumns) ||
                  skipExpenseSourceCells(colId)
              );

              for (let i = startIndex; i <= endIndex; i++) {
                const row = params.api.getModel().getRow(i);
                const rowData = row?.data;

                if (rowData) {
                  if (
                    skipNoNEditableDiscountCells(
                      params.column.getColId(),
                      rowData,
                      ctx.qcaComponent.editableTMAColumns
                    ) ||
                    skipExpenseSourceCells(params.column.getColId())
                  ) {
                    continue;
                  }

                  switch (params.column.getColId()) {
                    case 'historical_adjustment_amount':
                      ctx.rows.historicalAdjustmentChanged.add(rowData.activity_id);
                      ctx.updateDynamicFields(
                        0, // new value
                        rowData.activity_id,
                        params.column.getColId(),
                        rowData.tma_unit_cost,
                        0, // historical adjustment
                        rowData.historical_adjustment_amount, // old value
                        rowData.vendor_estimate_amount,
                        rowData.tma_unit,
                        rowData.tma_amount,
                        rowData.total_amount,
                        rowData.tma_source,
                        rowData
                      );
                      break;
                    case 'tma_unit':
                      ctx.rows.unitChanged.add(rowData.activity_id);
                      ctx.updateDynamicFields(
                        0, // new value
                        rowData.activity_id,
                        params.column.getColId(),
                        rowData.tma_unit_cost,
                        rowData.historical_adjustment_amount,
                        rowData.vendor_estimate_amount,
                        rowData.tma_unit, // old value
                        0, // number of units
                        rowData.tma_amount,
                        rowData.total_amount,
                        rowData.tma_source,
                        rowData
                      );
                      break;
                    case 'vendor_estimate_amount':
                      ctx.rows.vendorEstimateChanged.add({
                        activity_id: rowData.activity_id,
                        time: dayjs(),
                      });
                      ctx.updateDynamicFields(
                        0, // new value
                        rowData.activity_id,
                        params.column.getColId(),
                        rowData.tma_unit_cost,
                        rowData.historical_adjustment_amount, // historical adjustment
                        rowData.vendor_estimate_amount, // old value
                        0,
                        rowData.tma_unit,
                        rowData.tma_amount,
                        rowData.total_amount,
                        rowData.tma_source,
                        rowData
                      );
                      break;
                    case 'vendor_estimate_amount_ltd':
                      ctx.rows.vendorEstimateAmountLtdChanged.add({
                        activity_id: rowData.activity_id,
                        time: dayjs(),
                      });
                      ctx.updateDynamicFields(
                        0, // new value
                        rowData.activity_id,
                        params.column.getColId(),
                        rowData.tma_unit_cost,
                        rowData.historical_adjustment_amount, // historical adjustment
                        rowData.vendor_estimate_amount, // old value
                        0,
                        rowData.tma_unit,
                        rowData.tma_amount,
                        rowData.total_amount,
                        rowData.tma_source,
                        rowData
                      );
                      break;
                    case 'vendor_estimate_percent_ltd':
                      ctx.rows.vendorEstimatePercentChanged.add({
                        activity_id: rowData.activity_id,
                        time: dayjs(),
                      });
                      ctx.updateDynamicFields(
                        0, // new value
                        rowData.activity_id,
                        params.column.getColId(),
                        rowData.tma_unit_cost,
                        rowData.historical_adjustment_amount, // historical adjustment
                        rowData.vendor_estimate_amount, // old value
                        0,
                        rowData.tma_unit,
                        rowData.tma_amount,
                        rowData.total_amount,
                        rowData.tma_source,
                        rowData
                      );
                      break;
                    case 'tma_amount':
                    default:
                      ctx.rows.totalChanged.add(rowData.activity_id);
                      ctx.updateDynamicFields(
                        0, // new value
                        rowData.activity_id,
                        params.column.getColId(),
                        rowData.tma_unit_cost,
                        rowData.historical_adjustment_amount,
                        rowData.vendor_estimate_amount,
                        rowData.tma_amount, // old value
                        rowData.tma_unit,
                        0, // tma_amount,
                        rowData.total_amount,
                        rowData.tma_source,
                        rowData
                      );
                      break;
                  }

                  ctx.rows.edited.add(rowData.activity_id);
                }
              }
            },
            (colId: string, dData) =>
              skipNoNEditableDiscountCells(colId, dData, ctx.qcaComponent.editableTMAColumns) ||
              skipExpenseSourceCells(colId)
          );

          return true;
        default:
          return false;
      }
    }

    return false;
  };
